import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { CheckCircle2, Circle, MailCheck } from "lucide-react";

const PASSWORD_RULES = [
  { key: "length", label: "At least 12 characters", test: (p) => p.length >= 12 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { key: "number", label: "One number", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "One special character", test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(p) },
];

const FEATURES = [
  "Live classes with real instructors",
  "Small cohorts — max 8 students",
  "For children, teens, adults & seniors",
  "Verified completion certificates",
  "10 professional disciplines",
  "Kids Academy — ages 4 to 14",
  "Code playground — no install needed",
  "Free to register",
];

const COURSE_TILES = [
  { label: "Python",          color: "#3B6FD4", delay: 0 },
  { label: "Kids Coding",     color: "#C5A059", delay: 0.3 },
  { label: "DevOps",          color: "#2E5A44", delay: 0.6 },
  { label: "Robotics",        color: "#722F37", delay: 0.9 },
  { label: "Mathematics",     color: "#2C497F", delay: 1.2 },
  { label: "English",         color: "#8B2635", delay: 1.5 },
  { label: "QA Testing",      color: "#4A7C59", delay: 1.8 },
  { label: "Electronics",     color: "#B8860B", delay: 2.1 },
];

const ANIM_CSS = `
@keyframes fadeSlideIn {
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes floatY {
  0%,100% { transform: translateY(0px); }
  50%     { transform: translateY(-8px); }
}
@keyframes shimmer {
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
}
@keyframes checkIn {
  from { opacity:0; transform:scale(.6) translateX(-8px); }
  to   { opacity:1; transform:scale(1)  translateX(0); }
}
`;

export default function Register() {
  const { setError } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRef = params.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ref, setRef] = useState(initialRef);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendState, setResendState] = useState("idle"); // idle | busy | sent

  const ruleResults = useMemo(
    () => PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(password) })),
    [password]
  );
  const passwordValid = ruleResults.every((r) => r.ok);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!passwordValid) {
      setErr("Please meet all password requirements below.");
      return;
    }
    setBusy(true);
    try {
      const url = ref
        ? `/auth/register?ref=${encodeURIComponent(ref)}`
        : "/auth/register";
      const { data } = await api.post(url, { name, email, password });
      // Registration is two-step: this only creates a pending record and
      // sends a verification email. No account/cookies exist until the
      // link in that email is opened.
      setPendingEmail(data?.email || email);
    } catch (er) {
      setErr(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResendState("busy");
    try {
      await api.post("/auth/resend-verification", { email: pendingEmail });
    } catch {
      // Endpoint always returns ok — a failure here is a network issue, not a rejection.
    } finally {
      setResendState("sent");
    }
  };

  if (pendingEmail) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8" data-testid="register-pending">
        <div className="text-center max-w-sm">
          <div className="flex justify-center mb-6">
            <MailCheck size={48} className="text-eduhu-gold" />
          </div>
          <h1 className="font-serif text-4xl text-eduhu-dark">Check your email</h1>
          <p className="mt-4 text-eduhu-inkSoft">
            We sent a verification link to <b>{pendingEmail}</b>. Open it within 15 minutes
            to activate your account.
          </p>
          <button
            onClick={resend}
            disabled={resendState === "busy"}
            className="mt-8 eduhu-btn-primary"
          >
            {resendState === "busy" ? "Sending…" : resendState === "sent" ? "Email sent" : "Resend email"}
          </button>
          <div className="mt-6 text-sm text-eduhu-inkSoft">
            <Link to="/login" className="text-eduhu-claret nav-link">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2 border-b border-eduhu-border" data-testid="register-page">
      <style>{ANIM_CSS}</style>

      {/* ── Left form panel ───────────────────────────────────────────────── */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <div className="flex justify-center mb-8">
            <Link to="/" aria-label="EDUHUB home">
              <img src="/logo.png" alt="EDUHUB logo" className="w-16 h-16 object-contain" />
            </Link>
          </div>

          <div className="overline text-eduhu-claret mb-3">{t("auth.signup")}</div>
          <h1 className="font-serif text-4xl lg:text-5xl text-eduhu-dark tracking-tight">
            Begin your study.
          </h1>

          <label className="block mt-10">
            <div className="overline text-eduhu-inkSoft mb-2">{t("auth.name")}</div>
            <input
              data-testid="name-input"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 focus:outline-none focus:border-eduhu-dark"
            />
          </label>

          <label className="block mt-6">
            <div className="overline text-eduhu-inkSoft mb-2">{t("auth.email")}</div>
            <input
              data-testid="email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 focus:outline-none focus:border-eduhu-dark"
            />
          </label>

          <label className="block mt-6">
            <div className="overline text-eduhu-inkSoft mb-2">{t("auth.password")}</div>
            <input
              data-testid="password-input"
              type="password"
              required
              minLength={12}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setShowRules(true)}
              className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 focus:outline-none focus:border-eduhu-dark"
            />
            {showRules && (
              <ul className="mt-2 space-y-1" data-testid="password-rules">
                {ruleResults.map((r) => (
                  <li
                    key={r.key}
                    className={`flex items-center gap-1.5 text-xs ${r.ok ? "text-eduhu-success" : "text-eduhu-inkSoft"}`}
                  >
                    {r.ok ? <CheckCircle2 size={13} /> : <Circle size={13} />}
                    {r.label}
                  </li>
                ))}
              </ul>
            )}
          </label>

          <label className="block mt-6">
            <div className="overline text-eduhu-inkSoft mb-2">Referral code (optional)</div>
            <input
              data-testid="ref-input"
              value={ref}
              onChange={(e) => setRef(e.target.value.toUpperCase())}
              placeholder="A friend's code"
              className="w-full bg-eduhu-bg2 border border-eduhu-border px-4 py-3 focus:outline-none focus:border-eduhu-dark font-mono"
            />
            <div className="text-xs text-eduhu-inkSoft mt-1.5">
              Both of you get 10% off your first paid course.
            </div>
          </label>

          {err && (
            <div className="mt-4 text-sm text-eduhu-error" data-testid="register-error">{err}</div>
          )}

          <button
            data-testid="register-submit"
            disabled={busy}
            className="eduhu-btn-primary w-full justify-center mt-8"
          >
            {busy ? "…" : t("auth.signup")}
          </button>

          <div className="mt-6 text-sm text-eduhu-inkSoft text-center">
            {t("auth.haveAccount")}{" "}
            <Link to="/login" data-testid="to-login" className="text-eduhu-claret nav-link">
              {t("auth.signin")}
            </Link>
          </div>
        </form>
      </div>

      {/* ── Right decorative panel ────────────────────────────────────────── */}
      <div className="hidden lg:flex bg-eduhu-dark text-eduhu-bg grain border-l border-eduhu-border p-12 flex-col justify-between overflow-hidden relative">

        {/* Heading */}
        <div style={{ animation: "fadeSlideIn .7s ease both" }}>
          <div className="font-serif text-6xl text-eduhu-gold leading-none">
            A new<br />semester<br />
            <span className="italic text-eduhu-bg/70">awaits.</span>
          </div>
        </div>

        {/* Animated course discipline tiles */}
        <div className="flex-1 flex items-center py-8">
          <div className="w-full grid grid-cols-4 gap-2">
            {COURSE_TILES.map((tile) => (
              <div
                key={tile.label}
                style={{
                  animation: `floatY ${3 + tile.delay * 0.4}s ease-in-out infinite`,
                  animationDelay: `${tile.delay}s`,
                }}
                className="aspect-square flex flex-col items-center justify-center border border-white/10 text-center p-2 hover:border-eduhu-gold/40 transition-colors cursor-default"
              >
                <div
                  className="w-3 h-3 rounded-full mb-2"
                  style={{ background: tile.color, boxShadow: `0 0 8px ${tile.color}80` }}
                />
                <div className="text-[10px] text-eduhu-bg/70 uppercase tracking-widest leading-tight">
                  {tile.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Feature checklist */}
        <div style={{ animation: "fadeSlideIn .7s ease .4s both" }} className="space-y-2">
          {FEATURES.map((f, i) => (
            <div
              key={f}
              className="flex items-center gap-2 text-xs text-eduhu-bg/70"
              style={{ animation: `checkIn .4s ease ${.5 + i * .07}s both` }}
            >
              <CheckCircle2 size={12} className="text-eduhu-gold shrink-0" />
              {f}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
